import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { AgreementStatusBadge } from "./AgreementStatusBadge";
import { AgreementFormDialog } from "./AgreementFormDialog";
import {
  useRecurringAgreementMutations,
  type RecurringAgreementWithCustomer,
} from "@/hooks/use-recurring-agreements";
import { computeLineTotal, isExpiringSoon } from "@/lib/recurring-agreements-calc";
import { formatNOK, formatDate } from "@/lib/format";
import { osloDateString } from "@/lib/oslo-time";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";

const TYPE_LABEL: Record<string, string> = {
  recurring: "Løpende",
  rental: "Utleie",
  storage: "Lagring",
};

const INTERVAL_LABEL: Record<string, string> = {
  monthly: "Månedlig",
  quarterly: "Kvartalsvis",
  yearly: "Årlig",
};

interface AgreementsTableProps {
  agreements: RecurringAgreementWithCustomer[];
  /** Skjul kunde-kolonnen når tabellen er scopet til én kunde (kundekortet, VIB-1290). */
  hideCustomerColumn?: boolean;
  /** Kundekortet (wireframe): egen Autosend-kolonne og «Forny» på avtaler som utløper snart. */
  showAutoSend?: boolean;
  emptyMessage?: string;
}

export function AgreementsTable({
  agreements,
  hideCustomerColumn,
  showAutoSend = false,
  emptyMessage = "Ingen avtaler.",
}: AgreementsTableProps) {
  const { setAgreementStatus, softDeleteAgreement } = useRecurringAgreementMutations();
  const [editing, setEditing] = useState<RecurringAgreementWithCustomer | null>(null);
  const today = osloDateString(new Date());

  if (agreements.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">{emptyMessage}</p>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="border-b">
              {!hideCustomerColumn && (
                <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Kunde
                </th>
              )}
              <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Type</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Beskrivelse</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Intervall</th>
              <th className="text-right py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Beløp</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Neste kjøring</th>
              {showAutoSend && (
                <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Autosend</th>
              )}
              <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {agreements.map((a) => (
              <tr key={a.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                {!hideCustomerColumn && (
                  <td className="py-2.5 px-3 font-medium">{a.customer?.name ?? "-"}</td>
                )}
                <td className="py-2.5 px-3">{TYPE_LABEL[a.type] ?? a.type}</td>
                <td className="py-2.5 px-3">
                  {a.type === "storage"
                    ? `${a.storage_gb ?? 0} GB${a.storage_location ? ` · ${a.storage_location}` : ""}`
                    : a.description || a.rental_object || "-"}
                </td>
                <td className="py-2.5 px-3">{INTERVAL_LABEL[a.billing_interval] ?? a.billing_interval}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{formatNOK(computeLineTotal(a.lines))}</td>
                <td className="py-2.5 px-3 text-muted-foreground">{formatDate(a.next_run_date)}</td>
                {showAutoSend && <td className="py-2.5 px-3">{a.auto_send ? "På" : "Av"}</td>}
                <td className="py-2.5 px-3">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <AgreementStatusBadge status={a.status} />
                    {isExpiringSoon(a, today) && <StatusBadge tone="near" label="Utløper snart" />}
                  </span>
                </td>
                <td className="py-2.5 px-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Avtale-handlinger">
                        <SrIcon name="more" className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(a)}>
                        <SrIcon name="edit" className="w-3.5 h-3.5 mr-2" /> Rediger
                      </DropdownMenuItem>
                      {isExpiringSoon(a, today) && (
                        <DropdownMenuItem onClick={() => setEditing(a)}>
                          <SrIcon name="calendar" className="w-3.5 h-3.5 mr-2" /> Forny
                        </DropdownMenuItem>
                      )}
                      {a.status === "active" && (
                        <DropdownMenuItem
                          onClick={() => setAgreementStatus.mutate({ id: a.id, status: "paused" })}
                        >
                          <SrIcon name="pause" className="w-3.5 h-3.5 mr-2" /> Sett på pause
                        </DropdownMenuItem>
                      )}
                      {a.status === "paused" && (
                        <DropdownMenuItem
                          onClick={() => setAgreementStatus.mutate({ id: a.id, status: "active" })}
                        >
                          <SrIcon name="play" className="w-3.5 h-3.5 mr-2" /> Gjenoppta
                        </DropdownMenuItem>
                      )}
                      {a.status !== "ended" && (
                        <DropdownMenuItem
                          onClick={() => setAgreementStatus.mutate({ id: a.id, status: "ended" })}
                        >
                          <SrIcon name="x-circle" className="w-3.5 h-3.5 mr-2" /> Avslutt
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() =>
                          softDeleteAgreement.mutate({ id: a.id, customer_id: a.customer_id })
                        }
                      >
                        <SrIcon name="trash" className="w-3.5 h-3.5 mr-2" /> Slett
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <AgreementFormDialog
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
          agreement={editing}
        />
      )}
    </>
  );
}
